import { formatTimeValue } from '../../../../common/timeFormat';
/* eslint react/prop-types: 0 */
import React from 'react';
import { formatNumberValue } from '../../../../common/numberFormat';

//Date and URL Helpers

const formatDateOnly = (value) => {
  if (!value) return '-';
  if (typeof value !== 'string') return '-';

  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const [, y, mo, d] = m;
      const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
      return new Intl.DateTimeFormat(navigator.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      }).format(dt);
    }
  }

  const dt = new Date(value);
  if (isNaN(dt.getTime())) return '-';
  return new Intl.DateTimeFormat(navigator.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(dt);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(navigator.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const parseLinkValue = (raw) => {
  if (!raw) return { url: '', text: '' };
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return { url: '', text: '' };
    if (s.startsWith('{') && s.endsWith('}')) {
      try {
        const obj = JSON.parse(s);
        return { url: String(obj.url || ''), text: String(obj.text || '') };
      } catch {}
    }
    return { url: s, text: '' };
  }
  if (typeof raw === 'object') {
    return { url: String(raw.url || ''), text: String(raw.text || '') };
  }
  return { url: '', text: '' };
};
/**
 * @param {{
 * fieldId: string;
 * fieldType: "text" | "number" | "dropdown" | "multiselect" | "checkbox" | "dateonly" | "datetime | url"
 * values: Record<string, string | number | boolean>;
 * selectOptions?: string[];
 * }} props
 * @returns
 */
const RenderValue = ({ fieldId, fieldType, values, selectOptions, config }) => {
  /**
   * @param {boolean} value
   * @returns {JSX}
   */
  const renderBoolValue = (value) => {
    if (value === '') {
      return <span className="value">-</span>;
    }

    return String(value) === 'true' ? (
      <span className="value">Yesy</span>
    ) : (
      <span className="value">No</span>
    );
  };
  /**
   *
   * @param {string | number} value
   */
  const renderTextOrNumberValue = (value) => {
    // initial value from the server for number field is an empty string ¯\＿(ツ)＿/¯
    if (typeof value === 'number' || fieldType === 'number') {
      return value ? (
        <span className="value">{value}</span>
      ) : (
        <span className="value">{value === 0 ? '0' : '-'}</span>
      );
    }

    return value ? (
      <span className="value">{value}</span>
    ) : (
      <span className="value">-</span>
    );
  };

  const renderSelectValue = (value, selectOptions = []) => {
    const selectedValue = selectOptions.find((option) => option.key === value);

    if (!selectedValue) {
      return <span className="value">-</span>;
    }

    return <span className="value">{selectedValue.value}</span>;
  };

  const renderUrlValue = (value) => {
    if (!value) return <span className="value">-</span>;
    const { url, text } = parseLinkValue(value);
    if (!url) return <span className="value">-</span>;

    const label = (text || '').trim() || url;

    return (
      <a
        className="value"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={url}
      >
        {label}
      </a>
    );
  };

  const renderMultiselectValue = (value, selectOptions = []) => {
    if (!value || !Array.isArray(value) || !value.length) {
      return <span className="value">-</span>;
    }

    const selectedValues = [];

    for (const { key, value: optionName } of selectOptions) {
      const isSelected = value.includes(key);

      if (isSelected) {
        selectedValues.push(optionName);
      }
    }

    if (!selectedValues.length) {
      return <span className="value">-</span>;
    }

    return <span className="value">{selectedValues.join(', ')}</span>;
  };

  const renderDate = (value, isOnlyDate = true) => {
    const label = isOnlyDate ? formatDateOnly(value) : formatDateTime(value);
    return <span className="value">{label}</span>;
  };

  /**
   * @type {Record<typeof fieldType, (value, options = []) => JSX>}
   */
  const renderValueStrategy = {
    text: renderTextOrNumberValue,
    number: (value) => (
      <span className="value">
        <bdi>{formatNumberValue(value, config)}</bdi>
      </span>
    ),
    dropdown: renderSelectValue,
    multiselect: renderMultiselectValue,
    checkbox: renderBoolValue,
    time: (value) => (
      <span className="value">{formatTimeValue(value, config)}</span>
    ),
    dateonly: (value) => renderDate(value, true),
    datetime: (value) => renderDate(value, false),
    url: renderUrlValue
  };

  const renderFactory = renderValueStrategy[fieldType];

  if (!renderFactory) {
    return <div>Invalid field type</div>;
  }

  return renderFactory(values[fieldId], selectOptions);
};

export default RenderValue;
